import {useCallback, useEffect, useState} from 'react';
import {graphql, forecastFetchQuery, commitTicketingMutation} from '../../../graphql/forecastRelay';
import SavedFiltersCreateMutation from '../../../../../mutations/ticketing/tickets/savedFiltersCreateMutation';
import SavedFiltersDeleteMutation from '../../../../../mutations/ticketing/tickets/savedFiltersDeleteMutation';
import type {useSavedFiltersQuery$data} from './__generated__/useSavedFiltersQuery.graphql';

const SavedFiltersQuery = graphql`
	query useSavedFiltersQuery {
		viewer {
			savedFilters {
				id
				name
				criteria
			}
		}
	}
`;

export interface SavedFilter {
	id: string;
	name: string;
	criteria: Record<string, unknown>;
}

export function useSavedFilters(fetchKey?: number) {
	const [filters, setFilters] = useState<SavedFilter[]>([]);

	useEffect(() => {
		let cancelled = false;
		forecastFetchQuery(SavedFiltersQuery, {})
			.then(data => {
				if (cancelled) return;
				const typed = data as useSavedFiltersQuery$data | null;
				setFilters(
					(typed?.viewer?.savedFilters ?? []).map(f => ({
						id: f.id,
						name: f.name,
						criteria: (typeof f.criteria === 'string' ? JSON.parse(f.criteria) : f.criteria) as Record<
							string,
							unknown
						>,
					}))
				);
			})
			.catch(() => {
				if (cancelled) return;
				setFilters([]);
			});
		return () => {
			cancelled = true;
		};
	}, [fetchKey]);

	const createFilter = useCallback(
		(name: string, criteria: Record<string, unknown>): Promise<void> =>
			new Promise((resolve, reject) => {
				commitTicketingMutation(
					SavedFiltersCreateMutation,
					{name, criteria: JSON.stringify(criteria)},
					() => resolve(),
					err => reject(err)
				);
			}),
		[]
	);

	const deleteFilter = useCallback(
		(id: string): Promise<void> =>
			new Promise((resolve, reject) => {
				commitTicketingMutation(
					SavedFiltersDeleteMutation,
					{id},
					() => resolve(),
					err => reject(err)
				);
			}),
		[]
	);

	return {filters, createFilter, deleteFilter};
}
